import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _extends from '@babel/runtime/helpers/extends';
import { makeStyles, archerConstants, Typography, Tooltip, LayoutGrid, _DeprecatedCheckbox } from '@archer-technologies/ui-components';
import { VirtualTable, Table, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import { createElement, useRef, useState, useEffect, forwardRef } from 'react';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { TableHeaderRowCellFilter } from './filtering/tableHeaderRowFilter.plugin.js';
var _excluded = ["children"],
    _excluded2 = ["children", "filterProps"];
var useStyles = makeStyles(function () {
  return {
    gridTable: {
      borderCollapse: 'collapse',
      '& .MuiTableRow-root': {
        '& .MuiTableCell-root': {
          '& > div:nth-child(1)': {
            '& > button:nth-child(1)': {
              margin: 0
            }
          }
        }
      }
    },
    tableTitleText: {
      display: 'initial'
    },
    checkboxCellStyles: {
      background: 'inherit',
      textAlign: 'center',
      verticalAlign: 'top',
      '& .MuiCheckbox-root': {
        '&:hover': {
          backgroundColor: archerConstants.archerButtonHover
        },
        '&:active': {
          backgroundColor: archerConstants.archerButtonHover
        }
      }
    },
    textOverflow: {
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    actionColumnStyles: {
      '& [class*="Content"]': {
        justifyContent: 'center',
        '& span': {
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          '& span[class*="Title"]': {
            display: 'flex',
            justifyContent: 'center'
          },
          '& svg': {
            display: 'none'
          }
        }
      }
    }
  };
});

var TableComponent = function TableComponent(_ref) {
  var children = _ref.children,
      restProps = _objectWithoutProperties(_ref, _excluded); // This component returns the Table element of the VirtualTable component inside the grid.
  // In this case, we are forcing the table layout to auto, so that it properly sizes within the
  // context of flex containers.


  var _useStyles = useStyles(),
      gridTable = _useStyles.gridTable;

  return /*#__PURE__*/createElement(VirtualTable.Table, _extends({
    className: gridTable,
    style: {
      tableLayout: 'auto'
    }
  }, restProps), children);
};

var TableCellComponent = function TableCellComponent(props) {
  // This component may be used to manipulate cells in the Table component.
  var value = props.value,
      children = props.children;

  var _useStyles2 = useStyles(),
      textOverflow = _useStyles2.textOverflow;

  var ref = useRef(null);

  var _React$useState = useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isOverflown = _React$useState2[0],
      setIsOverflown = _React$useState2[1];

  useEffect(function () {
    if (ref.current) {
      setIsOverflown(ref.current.scrollWidth > ref.current.clientWidth);
    }
  });
  return /*#__PURE__*/createElement(Table.Cell, props, children || (typeof value === 'string' ? /*#__PURE__*/createElement(Typography, {
    variant: "body1",
    color: "textPrimary"
  }, /*#__PURE__*/createElement(Tooltip, {
    title: value,
    disableHoverListener: !isOverflown
  }, /*#__PURE__*/createElement("div", {
    className: textOverflow,
    ref: ref
  }, value))) : value));
};

var wrapLayoutStyles = function wrapLayoutStyles(children, isActionsColumn) {
  var _useStyles3 = useStyles(),
      actionColumnStyles = _useStyles3.actionColumnStyles;

  return isActionsColumn ? /*#__PURE__*/createElement(LayoutGrid, {
    className: isActionsColumn ? actionColumnStyles : '',
    container: true,
    wrap: "nowrap"
  }, children) : children;
};

var TableHeaderRowCellComponent = function TableHeaderRowCellComponent(_ref2) {
  var children = _ref2.children,
      filterProps = _ref2.filterProps,
      restProps = _objectWithoutProperties(_ref2, _excluded2); // This component may be used to manipulate cells in the TableHeaderRow component.


  var isActionsColumn = restProps.column.name === 'actions';
  return /*#__PURE__*/createElement(TableHeaderRow.Cell, restProps, wrapLayoutStyles(children, isActionsColumn), filterProps && /*#__PURE__*/createElement(TableHeaderRowCellFilter, filterProps), /*#__PURE__*/createElement("div", {
    className: "grid-table-header-row-cell-splitter"
  }));
};

var TableHeaderRowTitleComponent = function TableHeaderRowTitleComponent(_ref3) {
  var children = _ref3.children; // This component may be used to manipulate the titles in cells of the TableHeaderRow component.

  var _useStyles4 = useStyles(),
      tableTitleText = _useStyles4.tableTitleText;

  var title = (children === null || children === void 0 ? void 0 : children.toString()) || '';
  var CustomTitle = /*#__PURE__*/forwardRef(function (undefined$1, ref) {
    return /*#__PURE__*/createElement("div", {
      ref: ref
    }, /*#__PURE__*/createElement(Typography, {
      variant: "h6",
      color: "textPrimary",
      className: tableTitleText,
      align: "center"
    }, title));
  });
  return /*#__PURE__*/createElement(TableHeaderRow.Title, null, /*#__PURE__*/createElement(Tooltip, {
    title: title
  }, /*#__PURE__*/createElement(CustomTitle, null)));
};

var TableHeaderRowSortLabel = function TableHeaderRowSortLabel(props) {
  return /*#__PURE__*/createElement(TableHeaderRow.SortLabel, _extends({}, props, {
    className: "table-header-row-sort-label"
  }));
};

var TableHeaderRowGroupButtonComponent = function TableHeaderRowGroupButtonComponent(props) {
  var disabled = props.disabled;
  var className = 'table-header-row-group-button';

  if (disabled) {
    className += ' table-header-row-group-button-disabled';
  }

  return /*#__PURE__*/createElement(TableHeaderRow.GroupButton, _extends({}, props, {
    className: className
  }));
};

var TableRowSelectCellComponent = function TableRowSelectCellComponent(_ref4) {
  var row = _ref4.row,
      selected = _ref4.selected,
      onToggle = _ref4.onToggle;

  var _useStyles5 = useStyles(),
      checkboxCellStyles = _useStyles5.checkboxCellStyles;

  var items = [{
    description: "row-".concat(row.id, "-checkbox"),
    id: "row-".concat(row.id, "-checkbox"),
    isChecked: selected,
    isEnabled: row.disabledIndex === undefined ? true : row.disabledIndex
  }];
  var checkboxProps = {
    checkboxGroup: {
      items: items
    },
    onChange: function onChange() {
      return onToggle;
    },
    noFormControl: true
  };
  return /*#__PURE__*/createElement("td", {
    className: checkboxCellStyles
  }, /*#__PURE__*/createElement(_DeprecatedCheckbox, checkboxProps));
};

export { TableCellComponent, TableComponent, TableHeaderRowCellComponent, TableHeaderRowGroupButtonComponent, TableHeaderRowSortLabel, TableHeaderRowTitleComponent, TableRowSelectCellComponent };